import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { TokenStorageService } from './token-storage.service';
import { setUserTokenToLocalStorage, clearImpersonation, logoutAdmin, logoutImpersonatedUser } from '../store/auth/auth.actions';
import { isUserImpersonated } from '../store/auth/auth.selector';

@Injectable({
  providedIn: 'root'
})
export class ImpersonationService {

  constructor(
    private tokenStorageService: TokenStorageService,
    private store: Store
  ) {}

  /**
   * Check if current tab is in impersonation mode
   */
  isImpersonating(): boolean {
    return this.tokenStorageService.isImpersonationMode();
  }

  /**
   * Get observable for impersonation status
   */
  getImpersonationStatus(): Observable<boolean> {
    return this.store.select(isUserImpersonated);
  }

  /**
   * Start impersonation with the given token
   * This method is designed to work in a new tab without affecting the parent
   */
  startImpersonation(impersonateToken: string): void {
    // Set impersonation tokens in sessionStorage (tab-specific)
    this.tokenStorageService.setImpersonatedUserTokens(impersonateToken, impersonateToken);

    // Update the store
    this.store.dispatch(setUserTokenToLocalStorage({
      userToken: impersonateToken,
      userRefreshToken: impersonateToken
    }));
  }

  /**
   * End impersonation and return to admin session
   */
  endImpersonation(): void {
    this.store.dispatch(clearImpersonation());
  }

  /**
   * Generate impersonation URL for opening in new tab
   * If redirectTo is provided, the new tab will navigate there after setting the impersonation token
   */
  generateImpersonationUrl(impersonateToken: string, redirectTo?: string): string {
    const baseUrl = window.location.origin;
    const params = new URLSearchParams({
      impersonate_token: impersonateToken
    });

    if (redirectTo) {
      params.set('redirect_to', redirectTo);
    } else {
      // Legacy behaviour: force a reload to bootstrap the app
      params.set('force_reload', 'true');
    }

    return `${baseUrl}/blank-page?${params.toString()}`;
  }

  /**
   * Open impersonation in new tab
   */
  openImpersonationInNewTab(impersonateToken: string, redirectTo?: string): void {
    const impersonationUrl = this.generateImpersonationUrl(impersonateToken, redirectTo);
    window.open(impersonationUrl, '_blank');
  }

  /**
   * Get current active token (impersonated or admin)
   */
  getCurrentToken(): string | null {
    return this.tokenStorageService.getCurrentToken();
  }

  /**
   * Get admin tokens specifically
   */
  getAdminTokens() {
    return this.tokenStorageService.getAdminTokens();
  }

  /**
   * Get impersonated user tokens specifically
   */
  getImpersonatedTokens() {
    return this.tokenStorageService.getImpersonatedUserTokens();
  }

  /**
   * Handle logout based on current user type
   * Returns the appropriate redirect URL
   */
  handleLogout(): string {
    if (this.isImpersonating()) {
      return this.logoutImpersonatedUser();
    } else {
      return this.logoutAdmin();
    }
  }

  /**
   * Logout admin user - clears all sessions and redirects to login
   */
  private logoutAdmin(): string {
    this.store.dispatch(logoutAdmin());
    return '/auth/login';
  }

  /**
   * Logout impersonated user - returns to admin panel
   */
  private logoutImpersonatedUser(): string {
    this.store.dispatch(logoutImpersonatedUser());
    // Return to admin organisations users page specifically
    return '/admin/organisation-users';
  }

  /**
   * Check if we should force reload after logout
   */
  shouldForceReloadAfterLogout(): boolean {
    // Force reload when logging out from impersonated session to ensure clean state
    return this.isImpersonating();
  }
}
